import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MasterUtilities from "../../components/features/master-utilities/master-utilities";
import { MapLeftPanel } from "../../shared/map-left-panel/map-left-panel";
import styles from "./master-utilities-page.module.scss";
import { MapWrapper } from "../../shared/map-wrapper/map-wrapper";
import {
  MapSection,
  setEnableDrawingTools,
  setSection,
  stopDrawingTools,
} from "../../services/reducers/map";
import { initializeMap } from "../../services/actions/map";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import MapControls from "../../components/map-controls/map-controls";
import EditAttributesModal from "../../components/features/master-utilities/edit-attributes-modal/edit-attributes-modal";
import { resetMasterUtilitiesState } from "../../services/reducers/utilities-journal";
import {
  hasValidBuildingPermit,
  hasValidBuildingPermitForStage,
} from "../../utils/helpers-for-utilities";
import GeometryEditorWindow from "../../components/features/master-utilities/geometry-editor-window/geometry-editor-window";
import { useLock } from "../../components/features/master-utilities/useLock";

const MasterUtilitiesPage = () => {
  const { t } = useTranslation();
  const [leftPanelOpened, setLeftPanelOpened] = useState<boolean>(true);
  const [panelWidth, setPanelWidth] = useState<number>(0);
  const {
    visibleEditAttributes,
    uploadedFiles,
    stageTerritoryGeometry,
    selectedRow,
    buildingTerritoryGeometry,
  } = useAppSelector((state) => state.masterUtilities);
  const dispatch = useAppDispatch();

  const lock = useLock();

  const { mapMode, drawingToolsIsActive } = useAppSelector(
    (state) => state.map,
  );

  useEffect(() => {
    dispatch(setSection(MapSection.utilities));

    return () => {
      dispatch(initializeMap({ section: MapSection.none, mode: mapMode }));
      dispatch(resetMasterUtilitiesState());
      dispatch(setEnableDrawingTools(false));
      dispatch(stopDrawingTools());
    };
  }, []);

  useEffect(() => {
    dispatch(initializeMap({ section: MapSection.utilities, mode: mapMode }));

    if (mapMode === "3d") {
      if (drawingToolsIsActive) {
        dispatch(stopDrawingTools());
      }
      dispatch(setEnableDrawingTools(false));
    } else if (mapMode === "2d") {
      dispatch(setEnableDrawingTools(!lock?.drawingToolsButton));
    }
  }, [mapMode, lock?.drawingToolsButton]);

  const hasBuildingPermit = hasValidBuildingPermit(selectedRow);

  const hasDesignPermit = hasValidBuildingPermitForStage(selectedRow, "design");
  const hasAsBuiltPermit = hasValidBuildingPermitForStage(
    selectedRow,
    "asBuilt",
  );

  const shouldShowToast =
    (uploadedFiles.design.length > 0 && !stageTerritoryGeometry.design) ||
    (uploadedFiles.asBuilt.length > 0 && !stageTerritoryGeometry.asBuilt) ||
    (uploadedFiles.design.length > 0 &&
      stageTerritoryGeometry.design &&
      !buildingTerritoryGeometry.design &&
      hasDesignPermit) ||
    (uploadedFiles.asBuilt.length > 0 &&
      stageTerritoryGeometry.asBuilt &&
      !buildingTerritoryGeometry.asBuilt &&
      hasAsBuiltPermit);

  return (
    <div className={styles.masterUtilitiesPage}>
      <MapLeftPanel
        isExtendedWidth={true}
        onWidthChange={setPanelWidth}
        leftPanelOpened={leftPanelOpened}
        closeLeftPanel={setLeftPanelOpened}
      >
        <MasterUtilities />
      </MapLeftPanel>
      <MapWrapper
        panelWidth={panelWidth}
        containerId={`${MapSection.utilities}MapContainer`}
        leftPanelOpened={leftPanelOpened}
      >
        <div className={styles.editorToastContainer}>
          {shouldShowToast && (
            <div className={styles.toast}>
              {stageTerritoryGeometry && hasBuildingPermit
                ? t("master-utilities-page.drawBuildingArea")
                : t("master-utilities-page.drawTerritoryOfWork")}
            </div>
          )}
          <div className={styles.geometryWindowWrapper}>
            {drawingToolsIsActive && <GeometryEditorWindow />}
          </div>
        </div>
      </MapWrapper>
      <MapControls />
      {visibleEditAttributes && <EditAttributesModal />}
    </div>
  );
};

export default MasterUtilitiesPage;
